/** @file Composable for managing the Surface Add Post button functionality.
 * Provides the click handler, visibility logic, and animation/tooltip states.
 */
import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelCopyStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_copy_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { PostAttributes } from '@@/types'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { createSharedComposable } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const ADD_POST_BUTTON_CLICKED_STORAGE_KEY = 'hasAddPostButtonBeenClicked'
const useSurfaceAddPostButton = createSharedComposable(() => {
  const hasAddPostButtonBeenClicked = ref(safeLocalStorage.getItem(ADD_POST_BUTTON_CLICKED_STORAGE_KEY) === 'true')
  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceMapStore = useSurfaceMapStore()
  const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const surfaceDemoPadletPanelCopyStore = useSurfaceOnboardingDemoPadletPanelCopyStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  const { isMap } = storeToRefs(surfaceStore)
  const { startPickingLocation } = surfaceMapStore
  const { startNewPost } = surfacePostsStore
  const { currentUser } = storeToRefs(surfaceCurrentUserStore)
  const { isInReducedMotionMode } = useReducedMotionMode(currentUser)

  /**
   * Determines if the add post button can be shown.
   * Checks various conditions including permissions, layout, and modal states.
   */
  const canShowAddPost = computed(() => {
    const isAppWithNewPosting = device.app && appCan('newPostModal')
    return (
      !isAppWithNewPosting &&
      (surfaceStore.isEmbedded || !surfaceStore.isMobileLayout) &&
      surfacePermissionsStore.canIWrite &&
      !surfaceStore.isReadOnly &&
      !surfaceStore.isFrozen &&
      surfaceDraftsStore.hasFetchedInitialPostDrafts
    )
  })

  /**
   * Determines if the add post button should be visible.
   * Excludes submission request mode.
   */
  const xAddPostButton = computed(() => {
    return canShowAddPost.value && !surfaceStore.isSubmissionRequest
  })

  /**
   * Determines if the add post button should animate.
   * Disabled when in reduced motion mode, when a composer modal is active, or after the button has been clicked once.
   */
  const shouldAnimateAddPostButton = computed(() =>
    isInReducedMotionMode.value ? false : !hasAddPostButtonBeenClicked.value,
  )

  /**
   * Determines if the add post button should show its tooltip.
   * Only shown for non-map walls when there are no drafts.
   */
  const shouldShowAddPostButtonTooltip = computed(() => !isMap.value && surfaceDraftsStore.draftCids.length === 0)

  /**
   * Sets the add post button click status.
   * Updates local state and persists to localStorage.
   */
  const setAddPostButtonClickStatus = (): void => {
    if (hasAddPostButtonBeenClicked.value) return
    hasAddPostButtonBeenClicked.value = true
    safeLocalStorage.setItem(ADD_POST_BUTTON_CLICKED_STORAGE_KEY, 'true')
  }

  /**
   * Handles the click event on the add post button.
   * Opens map location picker for map walls, or starts a new post otherwise.
   */
  const handleAddPostButtonClick = (): void => {
    setAddPostButtonClickStatus()
    if (isMap.value) {
      startPickingLocation()
    } else {
      if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop && surfaceDemoPadletPanelStore.isPostStep) {
        surfaceDemoPadletPanelStore.setFavoritePlacesSectionAsMostRecentlyTouched()
        // Limit to one post draft at a time
        if (!surfaceDraftsStore.isAnyDraftActive) {
          const attributes: PostAttributes = {
            ...surfaceDemoPadletPanelCopyStore.postData[0],
          }
          startNewPost({ attributes })
        }
      } else {
        startNewPost({})
      }
    }
  }

  return {
    xAddPostButton,
    shouldAnimateAddPostButton,
    shouldShowAddPostButtonTooltip,
    handleAddPostButtonClick,
    setAddPostButtonClickStatus,
    hasAddPostButtonBeenClicked,
  }
})

export { useSurfaceAddPostButton }
